// Neutralises user-data metadata boxes (udta, meta) of an MP4 so location tags such as
// the (c)xyz atom never reach storage. The boxes are renamed to "free" (a skippable box)
// with their size untouched, so every chunk offset and the playback stay valid.

/** Largest moov box the browser is willing to load into memory. */
export const MAX_MOOV_BYTES = 64 * 1024 * 1024

const HEADER_BYTES = 16

function fourCC(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  )
}

function writeFourCC(view: DataView, offset: number, value: string): void {
  for (let i = 0; i < 4; i += 1) {
    view.setUint8(offset + i, value.charCodeAt(i))
  }
}

async function readBytes(blob: Blob, start: number, end: number): Promise<ArrayBuffer> {
  const slice = blob.slice(start, end)
  if (typeof slice.arrayBuffer === 'function') {
    return slice.arrayBuffer()
  }
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (): void => resolve(reader.result as ArrayBuffer)
    reader.onerror = (): void => reject(reader.error ?? new Error('read failed'))
    reader.readAsArrayBuffer(slice)
  })
}

interface BoxHeader {
  type: string
  /** Total box size including the header. */
  size: number
  headerSize: number
}

function readHeader(view: DataView, offset: number, available: number, extent: number): BoxHeader {
  if (available < 8) throw new Error('truncated box header')
  const size32 = view.getUint32(offset)
  const type = fourCC(view, offset + 4)
  if (size32 === 1) {
    if (available < 16) throw new Error('truncated large box header')
    const high = view.getUint32(offset + 8)
    const low = view.getUint32(offset + 12)
    return { type, size: high * 2 ** 32 + low, headerSize: 16 }
  }
  if (size32 === 0) {
    return { type, size: extent, headerSize: 8 }
  }
  return { type, size: size32, headerSize: 8 }
}

/** Renames udta/meta children of the container payload in place. Returns the rename count. */
function neutraliseChildren(view: DataView, payloadStart: number, payloadEnd: number, descendInto: string[]): number {
  let renamed = 0
  let offset = payloadStart
  while (offset < payloadEnd) {
    const header = readHeader(view, offset, payloadEnd - offset, payloadEnd - offset)
    if (header.size < header.headerSize || offset + header.size > payloadEnd) {
      throw new Error('invalid child box size')
    }
    if (header.type === 'udta' || header.type === 'meta') {
      writeFourCC(view, offset + 4, 'free')
      renamed += 1
    } else if (descendInto.includes(header.type)) {
      renamed += neutraliseChildren(view, offset + header.headerSize, offset + header.size, [])
    }
    offset += header.size
  }
  return renamed
}

/**
 * Returns a Blob with udta/meta boxes under moov and under each moov/trak renamed to
 * "free". The original file is returned unchanged when there is nothing to rename, when
 * moov is larger than 64 MB, or when parsing fails; the server inspection then decides.
 */
export async function stripMp4Metadata(file: Blob): Promise<Blob> {
  try {
    let offset = 0
    while (offset < file.size) {
      const headBuffer = await readBytes(file, offset, Math.min(file.size, offset + HEADER_BYTES))
      const headView = new DataView(headBuffer)
      const remaining = file.size - offset
      const header = readHeader(headView, 0, Math.min(remaining, headBuffer.byteLength), remaining)
      const boxSize = header.size
      if (boxSize < header.headerSize || offset + boxSize > file.size) return file

      if (header.type === 'moov') {
        if (boxSize > MAX_MOOV_BYTES) return file
        const moovBuffer = await readBytes(file, offset, offset + boxSize)
        const moovView = new DataView(moovBuffer)
        const renamed = neutraliseChildren(moovView, header.headerSize, boxSize, ['trak'])
        if (renamed === 0) return file
        return new Blob([file.slice(0, offset), moovBuffer, file.slice(offset + boxSize)], { type: file.type })
      }
      offset += boxSize
    }
    return file
  } catch {
    return file
  }
}
